<ng-container *ngIf="policy$ | async as policy">
  <vs-card class="overflow-hidden">
    <vs-card-header
      [config]="{
        title: config.page.title,
        subtitle: config.page.subtitle
      }"
    >
      <vs-badge *ngIf="dimensionsMeta$ | async as meta" slot="after-title">
        {{ meta.totalItems }} {{ meta.totalItems === 1 ? config.page.dimensionLabel : config.page.dimensionsLabel }}
      </vs-badge>

      <div *ngIf="canEdit$ | async" slot="end" class="flex gap-2">
        <button vs-button theme="primary" (click)="createDimension()">
          <vs-icon name="plus"></vs-icon>
          {{ config.common.addDimensionButton }}
        </button>
      </div>
    </vs-card-header>

    <vs-card-content class="!px-0 !pb-0 overflow-hidden !rounded-b-xl">
      <!-- Unified header row -->
      <div class="border-b border-gray-200 bg-gray-50 grid grid-cols-1 md:grid-cols-2">
        <div class="px-4 py-3">
          <h3 class="text-sm font-semibold text-gray-900">{{ config.page.dimensionsHeader }}</h3>
        </div>
        <ng-container *ngIf="selectedDimension$ | async as selectedDimension; else noDimensionHeader">
          <div class="px-4 py-3 flex items-center gap-2 justify-end md:justify-start md:border-l md:border-gray-200">
            <h3 class="text-sm font-semibold text-gray-900">{{ config.page.elementsHeader }}</h3>
            <vs-badge size="sm" [theme]="selectedDimension.isScalar ? 'primary' : 'default'">
              {{ selectedDimension.title }}
            </vs-badge>
            <vs-badge size="sm" theme="default">
              {{ selectedDimension.isScalar ? config.page.scalarType : config.page.categoricalType }}
            </vs-badge>
            <vs-badge *ngIf="selectedDimension.isPricing" size="sm" theme="success"> Pricing </vs-badge>
            <vs-badge *ngIf="elementsMeta$ | async as elementsMeta" size="sm" theme="default">
              {{ elementsMeta.totalItems }} {{ elementsMeta.totalItems === 1 ? config.page.elementLabel :
              config.page.elementsLabel }}
            </vs-badge>
            <div *ngIf="canEdit$ | async" class="flex gap-2 ml-2">
              <button vs-button appearance="clear" size="sm" (click)="clearSelection()">
                <vs-icon name="x-close" size="16"></vs-icon>
              </button>
              <button
                vs-button
                theme="primary"
                size="sm"
                (click)="createElement()"
                [disabled]="areElementsLocked(selectedDimension)"
              >
                <vs-icon name="plus" size="16"></vs-icon>
                {{ config.common.addElementButton }}
              </button>
            </div>
          </div>
        </ng-container>
        <ng-template #noDimensionHeader>
          <div class="px-4 py-3 md:border-l md:border-gray-200">
            <h3 class="text-sm font-semibold text-gray-900">{{ config.page.elementsHeader }}</h3>
          </div>
        </ng-template>
      </div>

      <!-- Responsive layout -->
      <div class="flex flex-col md:flex-row md:h-[600px]">
        <!--  Dimensions -->
        <div
          class="w-full md:w-1/2 md:border-r border-b md:border-b-0 border-gray-200 flex flex-col h-[400px] md:h-auto"
        >
          <!-- Filters -->
          <div class="flex gap-3 px-4 py-3 border-b border-gray-200">
            <vs-select
              vsInput
              class="flex-1"
              [value]="dimensionTypeFilter"
              [config]="{ placeholder: config.page.allTypesOption }"
              (valueChanges)="onTypeFilterChange($any($event))"
            >
              <li vs-select-option value="all">{{ config.page.allTypesOption }}</li>
              <li vs-select-option value="scalar">{{ config.page.scalarOnlyOption }}</li>
              <li vs-select-option value="categorical">{{ config.page.categoricalOnlyOption }}</li>
            </vs-select>

            <vs-input-container class="flex-1">
              <vs-icon name="search-lg" slot="start"></vs-icon>
              <input
                #searchInput
                vsInput
                (keyup)="searchDimensions(searchInput.value)"
                [placeholder]="config.page.searchPlaceholder"
              />
            </vs-input-container>
          </div>

          <!-- Dimensions Table -->
          <div class="flex-1 overflow-auto">
            <vs-connected-table
              #dimensionsTable
              endpoint="/v1/rating/dimensions"
              [columns]="(dimensionsColumns$ | async) ?? []"
              [cardContainer]="false"
              (rowClicked)="selectDimension($any($event))"
            >
              <div slot="empty" class="text-center py-8">
                <p class="text-gray-600">{{ config.page.noDimensionsFound }}</p>
                <p class="text-sm text-gray-500 mt-2">{{ config.page.noDimensionsHint }}</p>
              </div>
            </vs-connected-table>
          </div>
        </div>

        <!-- BOTTOM/RIGHT: Elements -->
        <div class="w-full md:w-1/2 flex flex-col h-[400px] md:h-auto">
          <ng-container *ngIf="selectedDimension$ | async as selectedDimension; else noDimensionSelected">
            <!-- Elements Filters -->
            <div class="flex gap-3 px-4 py-3 border-b border-gray-200">
              <vs-input-container class="flex-1">
                <vs-icon name="search-lg" slot="start"></vs-icon>
                <input
                  #elementSearchInput
                  vsInput
                  [value]="elementSearchInputValue"
                  (keyup)="filterElements(elementSearchInput.value)"
                  [placeholder]="config.page.searchPlaceholder"
                />
              </vs-input-container>
            </div>

            <!-- Drill breadcrumb (non-scalar only) -->
            <div
              *ngIf="!selectedDimension.isScalar"
              class="px-4 py-2 border-b border-gray-200 bg-white flex items-center gap-2"
            >
              <button
                vs-button
                appearance="clear"
                size="sm"
                class="!px-2"
                (click)="resetDrill()"
                [disabled]="(drillPath$ | async)?.length === 0"
              >
                All
              </button>

              <ng-container *ngIf="drillPath$ | async as path">
                <ng-container *ngFor="let p of path; let i = index">
                  <span class="text-gray-400">/</span>
                  <button
                    vs-button
                    appearance="clear"
                    size="sm"
                    class="!px-2"
                    (click)="jumpTo(p.id)"
                    [disabled]="i === path.length - 1"
                  >
                    {{ p.title }}
                  </button>
                </ng-container>

                <div class="ml-auto" *ngIf="path.length > 0">
                  <button vs-button appearance="clear" size="sm" class="!px-2" (click)="goUp()">
                    <vs-icon name="arrow-left" size="16"></vs-icon>
                    Back
                  </button>
                </div>
              </ng-container>
            </div>

            <!-- Elements Content -->
            <div class="flex-1 overflow-auto">
              <ng-container *ngIf="(loadingElements$ | async) === false; else loadingState">
                <div *ngIf="(displayedElements$ | async)?.length; else noElements">
                  <vs-table
                    [data]="displayedElements$ | async"
                    [columns]="(elementsColumns$ | async) ?? []"
                    [config]="{ draggable: (canDragElements$ | async) || false }"
                    (rowDragged)="reorderElements($event)"
                  ></vs-table>
                </div>
              </ng-container>

              <ng-template #loadingState>
                <div class="flex items-center justify-center py-12">
                  <vs-icon name="loading-01" class="text-primary-500 animate-spin" size="35"></vs-icon>
                </div>
              </ng-template>

              <ng-template #noElements>
                <div class="flex flex-col items-center justify-center gap-3 py-12 px-6">
                  <vs-featured-icon name="layers-three-01" theme="default"></vs-featured-icon>

                  <ng-container *ngIf="((elementSearchTerm$ | async) ?? '').trim(); else emptyLevelState">
                    <p class="text-sm font-semibold text-gray-900">{{ config.page.noMatchingElementsTitle }}</p>
                    <p class="text-sm text-gray-600 text-center max-w-sm">{{ config.page.noMatchingElementsHint }}</p>
                  </ng-container>

                  <ng-template #emptyLevelState>
                    <!-- Drilled into parent with no children -->
                    <ng-container *ngIf="(drillPath$ | async)?.length; else rootLevel">
                      <p class="text-sm font-semibold text-gray-900">No child elements yet</p>
                      <p class="text-sm text-gray-600 text-center max-w-sm">
                        This element doesn't have any child elements yet. Click below to add the first child.
                      </p>
                      <button
                        *ngIf="canEdit$ | async"
                        vs-button
                        theme="primary"
                        size="sm"
                        (click)="createElement()"
                        [disabled]="areElementsLocked(selectedDimension)"
                      >
                        <vs-icon name="plus"></vs-icon>
                        Add Child Element
                      </button>
                    </ng-container>

                    <!-- Root level with no elements -->
                    <ng-template #rootLevel>
                      <p class="text-sm font-semibold text-gray-900">{{ config.page.noElementsYet }}</p>
                      <p class="text-sm text-gray-600 text-center max-w-sm">
                        {{ selectedDimension.isScalar ? config.page.noElementsScalarHint :
                        config.page.noElementsCategoricalHint }}
                      </p>
                      <button
                        *ngIf="canEdit$ | async"
                        vs-button
                        theme="primary"
                        size="sm"
                        (click)="createElement()"
                        [disabled]="areElementsLocked(selectedDimension)"
                      >
                        <vs-icon name="plus"></vs-icon>
                        {{ config.common.addFirstElementButton }}
                      </button>
                    </ng-template>
                  </ng-template>
                </div>
              </ng-template>
            </div>

            <ng-container *ngIf="elementsPaginationConfig$ | async as paginationConfig">
              <vs-pagination
                *ngIf="paginationConfig.totalItems > 0"
                [config]="paginationConfig"
                (pagination)="onElementsPaginationChange($event)"
              ></vs-pagination>
            </ng-container>
          </ng-container>

          <ng-template #noDimensionSelected>
            <div class="flex-1 flex flex-col items-center justify-center gap-4 px-6 bg-white">
              <vs-featured-icon
                name="cursor-click-02"
                theme="default"
                class="!w-20 !h-20 [&>div]:!w-16 [&>div]:!h-16"
              ></vs-featured-icon>
              <p class="text-base font-semibold !text-gray-900">{{ config.page.selectDimensionTitle }}</p>
              <p class="text-base !text-gray-600 text-center max-w-sm">{{ config.page.selectDimensionHint }}</p>
            </div>
          </ng-template>
        </div>
      </div>
    </vs-card-content>
  </vs-card>

  <!-- Dialogs -->
  <ws-dimension-dialog #dimensionDialog (closed)="onDimensionDialogClosed($event)"></ws-dimension-dialog>
  <ws-element-dialog #elementDialog (closed)="onElementDialogClosed($event)"></ws-element-dialog>
  <ws-delete-dimension-dialog
    #deleteDimensionDialog
    (closed)="onDeleteDimensionDialogClosed($event)"
  ></ws-delete-dimension-dialog>
  <ws-delete-element-dialog
    #deleteElementDialog
    (closed)="onDeleteElementDialogClosed($event)"
  ></ws-delete-element-dialog>
</ng-container>
